import { normalizeBrandMentionKey, uniqueBrandMentions } from "./brandMentionAggregation";
import type { AccountEntityRole } from "../types/competitiveSetup";

export type TrustedEntityGovernanceRow = Pick<
  AccountEntityRole,
  | "id"
  | "raw_entity_name"
  | "normalized_entity_name"
  | "parent_entity_name"
  | "entity_role"
  | "score_treatment"
  | "confidence_score"
  | "source"
  | "approved_at"
  | "is_active"
> & {
  raw_entity_key?: string | null;
  normalized_entity_key?: string | null;
  review_status?: string | null;
};

export type EntityGovernanceBucketStatus = "mapped" | "suppressed" | "unresolved";

export type EntityGovernanceBucket = {
  canonicalKey: string;
  canonicalName: string;
  displayName: string;
  mappingStatus: EntityGovernanceBucketStatus;
  mapping_status: EntityGovernanceBucketStatus;
  reviewBadgeLabel: string | null;
  count: number;
  status: EntityGovernanceBucketStatus;
  entityRole: string | null;
  scoreTreatment: string | null;
  confidenceScore: number | null;
  source: string | null;
  rawNames: string[];
  rawExamples: string[];
  matchedGovernanceRows: number;
  isTargetBrand: boolean;
};

export type EntityGovernanceHealth = {
  totalRawMentions: number;
  mappedMentions: number;
  suppressedMentions: number;
  unresolvedMentions: number;
  mappedEntityBuckets: number;
  suppressedEntityBuckets: number;
  unresolvedEntityBuckets: number;
  duplicateAliasCandidates: number;
  lowConfidenceMappings: number;
  directCompetitorMappings: number;
  targetBrandMappings: number;
};

export type EntityGovernanceRollupResult = {
  displayEntities: EntityGovernanceBucket[];
  suppressedEntities: EntityGovernanceBucket[];
  health: EntityGovernanceHealth;
};

export const ENTITY_AUTO_RESOLUTION_CONFIDENCE_THRESHOLD = 0.9;

const MANUAL_GOVERNANCE_SOURCES = new Set([
  "manual_admin",
  "manual_admin_fix",
  "human_admin"
]);

const VALID_ENTITY_ROLES = new Set([
  "direct_competitor",
  "adjacent_competitor",
  "platform",
  "partner",
  "integration",
  "product",
  "customer",
  "publication",
  "generic_tool",
  "suppress",
  "unknown"
]);

const VALID_SCORE_TREATMENTS = new Set([
  "include_primary",
  "include_secondary",
  "show_only",
  "suppress_from_scoring",
  "suppress_everywhere"
]);

type GovernanceCandidate = TrustedEntityGovernanceRow & {
  rawKey: string;
  normalizedKey: string;
  parentKey: string;
  canonicalName: string;
  canonicalKey: string;
  entityRole: string | null;
  scoreTreatment: string | null;
  confidenceScore: number | null;
  source: string | null;
  isSuppressed: boolean;
};

export type EntityAutoResolutionInput = {
  confidenceScore: number | null | undefined;
  entityRole: string | null | undefined;
  canonicalEntityKey?: string | null;
  knownCustomerEntityKey?: string | null;
  isObviousNonMarketEntity?: boolean;
};

/** Future Agent Smith/runner boundary for automatic alias actions. */
export function canAutoResolveEntityAlias({
  confidenceScore,
  entityRole,
  canonicalEntityKey,
  knownCustomerEntityKey,
  isObviousNonMarketEntity = false
}: EntityAutoResolutionInput): boolean {
  if (
    confidenceScore == null ||
    !Number.isFinite(confidenceScore) ||
    confidenceScore < ENTITY_AUTO_RESOLUTION_CONFIDENCE_THRESHOLD
  ) {
    return false;
  }

  const normalizedRole = String(entityRole || "").trim().toLowerCase();
  const normalizedCanonicalKey = normalizeValue(canonicalEntityKey);
  const normalizedCustomerKey = normalizeValue(knownCustomerEntityKey);

  if (normalizedRole === "unknown") return false;

  if (["customer", "self", "target_brand"].includes(normalizedRole)) {
    return Boolean(
      normalizedCustomerKey && normalizedCanonicalKey === normalizedCustomerKey
    );
  }

  if (normalizedRole === "suppress") return isObviousNonMarketEntity;

  return normalizedCanonicalKey !== normalizedCustomerKey;
}

type AggregatedBucket = {
  canonicalKey: string;
  canonicalName: string;
  status: EntityGovernanceBucketStatus;
  reviewBadgeLabel: string | null;
  entityRole: string | null;
  scoreTreatment: string | null;
  confidenceScore: number | null;
  source: string | null;
  rawNames: Set<string>;
  matchedGovernanceRows: Set<string>;
  count: number;
  isTargetBrand: boolean;
};

function normalizeValue(value: unknown): string {
  return normalizeBrandMentionKey(value);
}

export function normalizeEntityGovernanceRow(
  row: TrustedEntityGovernanceRow
): TrustedEntityGovernanceRow {
  return {
    ...row,
    raw_entity_key:
      row.raw_entity_key || normalizeValue(row.raw_entity_name),
    normalized_entity_key:
      row.normalized_entity_key || normalizeValue(row.normalized_entity_name)
  };
}

function hasValidGovernanceShape(row: TrustedEntityGovernanceRow): boolean {
  return Boolean(
    VALID_ENTITY_ROLES.has(String(row.entity_role || "")) &&
      VALID_SCORE_TREATMENTS.has(String(row.score_treatment || "")) &&
      (!row.review_status || row.review_status === "approved")
  );
}

export function isTrustedEntityGovernanceRow(
  row: TrustedEntityGovernanceRow
): row is TrustedEntityGovernanceRow {
  const normalizedRow = normalizeEntityGovernanceRow(row);

  return Boolean(
    normalizedRow &&
      normalizedRow.is_active &&
      hasValidGovernanceShape(normalizedRow) &&
      (normalizedRow.approved_at != null ||
        MANUAL_GOVERNANCE_SOURCES.has(normalizedRow.source || "") ||
        (normalizedRow.confidence_score != null &&
          Number.isFinite(normalizedRow.confidence_score) &&
          normalizedRow.confidence_score >= ENTITY_AUTO_RESOLUTION_CONFIDENCE_THRESHOLD &&
          normalizedRow.entity_role !== "unknown"))
  );
}

function sourcePriority(source: string | null): number {
  switch (source) {
    case "manual_admin":
    case "manual_admin_fix":
    case "human_admin":
      return 4;
    case "approved":
      return 3;
    case "agent":
    case "agent_generated":
      return 2;
    case "seed":
      return 1;
    default:
      return 0;
  }
}

function rolePriority(entityRole: string | null): number {
  switch (entityRole) {
    case "customer":
      return 7;
    case "direct_competitor":
      return 6;
    case "adjacent_competitor":
      return 5;
    case "platform":
      return 4;
    case "partner":
      return 4;
    case "integration":
      return 3;
    case "product":
      return 3;
    case "publication":
      return 2;
    case "generic_tool":
      return 1;
    case "unknown":
      return 0;
    case "suppress":
      return -1;
    default:
      return 0;
  }
}

function treatmentPriority(scoreTreatment: string | null): number {
  switch (scoreTreatment) {
    case "include_primary":
      return 4;
    case "include_secondary":
      return 3;
    case "show_only":
      return 2;
    case "suppress_from_scoring":
      return 1;
    case "suppress_everywhere":
      return 0;
    default:
      return 0;
  }
}

function candidateScore(candidate: GovernanceCandidate, mentionKey: string): number[] {
  const exactNormalizedMatch = candidate.normalizedKey === mentionKey ? 1 : 0;
  const exactRawMatch = candidate.rawKey === mentionKey ? 1 : 0;
  const exactParentMatch = candidate.parentKey === mentionKey ? 1 : 0;

  return [
    candidate.isSuppressed ? 0 : 1,
    exactNormalizedMatch,
    exactRawMatch,
    exactParentMatch,
    candidate.confidenceScore ?? -1,
    sourcePriority(candidate.source),
    treatmentPriority(candidate.scoreTreatment),
    rolePriority(candidate.entityRole)
  ];
}

function compareScores(left: number[], right: number[]): number {
  for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
    const leftValue = left[index] ?? 0;
    const rightValue = right[index] ?? 0;

    if (leftValue !== rightValue) {
      return leftValue > rightValue ? 1 : -1;
    }
  }

  return 0;
}

function createGovernanceCandidates(
  rows: readonly TrustedEntityGovernanceRow[]
): GovernanceCandidate[] {
  const trustedRows = rows
    .map(normalizeEntityGovernanceRow)
    .filter(isTrustedEntityGovernanceRow);

  return trustedRows.map((row) => {
    const rawKey = row.raw_entity_key || normalizeValue(row.raw_entity_name);
    const normalizedKey =
      row.normalized_entity_key || normalizeValue(row.normalized_entity_name);
    const parentKey = normalizeValue(row.parent_entity_name);
    const canonicalName =
      row.normalized_entity_name?.trim() ||
      row.raw_entity_name?.trim() ||
      "";
    const canonicalKey = normalizeValue(canonicalName);

    return {
      ...row,
      rawKey,
      normalizedKey,
      parentKey,
      canonicalName,
      canonicalKey,
      entityRole: row.entity_role,
      scoreTreatment: row.score_treatment,
      confidenceScore: row.confidence_score,
      source: row.source,
      isSuppressed:
        row.score_treatment === "suppress_everywhere" || row.entity_role === "suppress"
    };
  });
}

function isCandidateEligibleForAutomaticRollup(
  candidate: GovernanceCandidate,
  customerKey: string
): boolean {
  if (
    MANUAL_GOVERNANCE_SOURCES.has(candidate.source || "") ||
    candidate.source === "account_context"
  ) {
    return true;
  }

  return canAutoResolveEntityAlias({
    confidenceScore: candidate.confidenceScore,
    entityRole: candidate.entityRole,
    canonicalEntityKey: candidate.canonicalKey,
    knownCustomerEntityKey: customerKey,
    isObviousNonMarketEntity: candidate.isSuppressed
  });
}

function matchGovernanceCandidate(
  mentionKey: string,
  customerKey: string,
  customerBrand: string | null | undefined,
  candidates: readonly GovernanceCandidate[]
): GovernanceCandidate | null {
  const scopedCandidates = candidates.filter((candidate) => {
    if (candidate.entityRole === "unknown") {
      return false;
    }

    if (!isCandidateEligibleForAutomaticRollup(candidate, customerKey)) {
      return false;
    }

    if (
      customerKey &&
      candidate.canonicalKey === customerKey &&
      candidate.entityRole !== "customer" &&
      candidate.source !== "account_context"
    ) {
      return false;
    }

    return (
      candidate.rawKey === mentionKey ||
      candidate.normalizedKey === mentionKey ||
      candidate.parentKey === mentionKey
    );
  });

  const targetScopedCandidates =
    customerKey && mentionKey === customerKey
      ? [
          {
            id: "customer-context",
            account_id: "",
            raw_entity_name: customerBrand?.trim() || customerKey,
            normalized_entity_name: customerBrand?.trim() || customerKey,
            parent_entity_name: null,
            entity_role: "customer",
            score_treatment: "include_primary",
            confidence_score: 1,
            approved_at: new Date().toISOString(),
            is_active: true,
            rawKey: customerKey,
            normalizedKey: customerKey,
            parentKey: "",
            canonicalName: customerBrand?.trim() || customerKey,
            canonicalKey: customerKey,
            entityRole: "customer",
            scoreTreatment: "include_primary",
            confidenceScore: 1,
            source: "account_context",
            isSuppressed: false
          } as GovernanceCandidate
        ]
      : [];

  const combined = [...targetScopedCandidates, ...scopedCandidates];

  if (!combined.length) return null;

  const unsuppressed = combined.filter((candidate) => !candidate.isSuppressed);
  const pool = unsuppressed.length ? unsuppressed : combined;

  return pool.reduce((best, candidate) => {
    if (!best) return candidate;

    const bestScore = candidateScore(best, mentionKey);
    const nextScore = candidateScore(candidate, mentionKey);

    return compareScores(nextScore, bestScore) > 0 ? candidate : best;
  }, null as GovernanceCandidate | null);
}

function ensureBucket(
  buckets: Map<string, AggregatedBucket>,
  key: string,
  canonicalName: string,
  status: EntityGovernanceBucketStatus,
  candidate?: GovernanceCandidate,
  isTargetBrand = false,
  surface: "customer" | "internal_admin" = "customer"
): AggregatedBucket {
  const existing = buckets.get(key);

  if (existing) {
    return existing;
  }

  const bucket: AggregatedBucket = {
    canonicalKey: key,
    canonicalName,
    status,
    reviewBadgeLabel:
      status === "unresolved" && surface === "internal_admin" ? "Needs review" : null,
    entityRole: candidate?.entityRole || null,
    scoreTreatment: candidate?.scoreTreatment || null,
    confidenceScore: candidate?.confidenceScore ?? null,
    source: candidate?.source || null,
    rawNames: new Set<string>(),
    matchedGovernanceRows: new Set<string>(),
    count: 0,
    isTargetBrand
  };

  buckets.set(key, bucket);
  return bucket;
}

function toBucketArray(buckets: Iterable<AggregatedBucket>): EntityGovernanceBucket[] {
  return Array.from(buckets)
    .map((bucket) => ({
      canonicalKey: bucket.canonicalKey,
      canonicalName: bucket.canonicalName,
      displayName: bucket.canonicalName,
      mappingStatus: bucket.status,
      mapping_status: bucket.status,
      reviewBadgeLabel: bucket.reviewBadgeLabel,
      count: bucket.count,
      status: bucket.status,
      entityRole: bucket.entityRole,
      scoreTreatment: bucket.scoreTreatment,
      confidenceScore: bucket.confidenceScore,
      source: bucket.source,
      rawNames: Array.from(bucket.rawNames),
      rawExamples: Array.from(bucket.rawNames).slice(0, 3),
      matchedGovernanceRows: bucket.matchedGovernanceRows.size,
      isTargetBrand: bucket.isTargetBrand
    }))
    .sort((left, right) =>
      right.count - left.count ||
      left.displayName.localeCompare(right.displayName, undefined, { sensitivity: "base" })
    );
}

export function aggregateBrandMentionsByGovernance<T>(
  rows: readonly T[],
  getBrandMentions: (row: T) => unknown,
  governanceRows: readonly TrustedEntityGovernanceRow[],
  customerBrand?: string | null,
  options?: {
    surface?: "customer" | "internal_admin";
  }
): EntityGovernanceRollupResult {
  const customersKey = normalizeValue(customerBrand);
  const surface = options?.surface || "customer";
  const candidates = createGovernanceCandidates(governanceRows);
  const buckets = new Map<string, AggregatedBucket>();
  const suppressedBuckets = new Map<string, AggregatedBucket>();

  let totalRawMentions = 0;
  let mappedMentions = 0;
  let unresolvedMentions = 0;
  let suppressedMentions = 0;

  rows.forEach((row) => {
    const responseBucketKeys = new Set<string>();

    uniqueBrandMentions(getBrandMentions(row)).forEach(({ key, brand }) => {
      if (!key) {
        return;
      }

      const matched = matchGovernanceCandidate(key, customersKey, customerBrand, candidates);

      totalRawMentions += 1;

      if (!matched) {
        const unresolvedKey = `unresolved:${key}`;
        if (responseBucketKeys.has(unresolvedKey)) {
          return;
        }

        responseBucketKeys.add(unresolvedKey);
        const bucket = ensureBucket(
          buckets,
          unresolvedKey,
          brand,
          "unresolved",
          undefined,
          false,
          surface
        );

        bucket.rawNames.add(brand);
        bucket.count += 1;
        unresolvedMentions += 1;
        return;
      }

      const canonicalName = matched.canonicalName || matched.normalized_entity_name || brand;
      const canonicalKey = matched.canonicalKey || normalizeValue(canonicalName) || key;
      const bucketKey = `${matched.isSuppressed ? "suppressed" : "mapped"}:${canonicalKey}`;

      if (responseBucketKeys.has(bucketKey)) {
        const existingBucket = matched.isSuppressed
          ? suppressedBuckets.get(bucketKey) || null
          : buckets.get(bucketKey) || null;

        if (existingBucket) {
          existingBucket.rawNames.add(brand);
          existingBucket.matchedGovernanceRows.add(
            `${matched.raw_entity_name}::${matched.normalized_entity_name}::${matched.entityRole}`
          );
        }

        return;
      }

      responseBucketKeys.add(bucketKey);

      const bucket = ensureBucket(
        matched.isSuppressed ? suppressedBuckets : buckets,
        bucketKey,
        canonicalName,
        matched.isSuppressed ? "suppressed" : "mapped",
        matched,
        customersKey ? normalizeValue(canonicalName) === customersKey : false,
        surface
      );

      bucket.rawNames.add(brand);
      bucket.matchedGovernanceRows.add(
        `${matched.raw_entity_name}::${matched.normalized_entity_name}::${matched.entityRole}`
      );

      if (matched.isSuppressed) {
        bucket.count += 1;
        suppressedMentions += 1;
      } else {
        bucket.count += 1;
        mappedMentions += 1;
      }
    });
  });

  const displayEntities = toBucketArray(buckets.values());
  const suppressedEntities = toBucketArray(suppressedBuckets.values());

  const duplicateAliasCandidates = displayEntities.filter(
    (bucket) => bucket.rawNames.length > 1 || bucket.matchedGovernanceRows > 1
  ).length;

  const lowConfidenceMappings = displayEntities.filter(
    (bucket) =>
      bucket.status === "mapped" &&
      bucket.confidenceScore != null &&
      bucket.confidenceScore < ENTITY_AUTO_RESOLUTION_CONFIDENCE_THRESHOLD
  ).length;

  const directCompetitorMappings = displayEntities.filter((bucket) =>
    ["direct_competitor", "adjacent_competitor"].includes(bucket.entityRole || "")
  ).length;

  const targetBrandMappings = displayEntities.filter((bucket) => {
    if (bucket.isTargetBrand) return true;
    return (bucket.entityRole || "") === "customer";
  }).length;

  return {
    displayEntities,
    suppressedEntities,
    health: {
      totalRawMentions,
      mappedMentions,
      suppressedMentions,
      unresolvedMentions,
      mappedEntityBuckets: displayEntities.filter((bucket) => bucket.status === "mapped").length,
      suppressedEntityBuckets: suppressedEntities.length,
      unresolvedEntityBuckets: displayEntities.filter((bucket) => bucket.status === "unresolved").length,
      duplicateAliasCandidates,
      lowConfidenceMappings,
      directCompetitorMappings,
      targetBrandMappings
    }
  };
}
